import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createRouter, RouterProvider } from "@tanstack/react-router";

import { BrandingThemeProvider } from "@/components/BrandingThemeProvider";
import { MfaVerifyProvider } from "@/components/auth/MfaVerifyDialog";
import { setMfaPromptHandler } from "@/lib/api";
import { useMfaVerifyPrompt } from "@/lib/hooks/mfa-verify-prompt";
import { queryClient } from "@/lib/queryClient";
import { routeTree } from "@/routeTree.gen";
import { useEffect } from "react";

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  context: { queryClient },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

/**
 * Wires the MFA prompt context into the imperative `apiRequest` client.
 * On mount: register the handler. Any 403 mfa_required emitted by apiRequest
 * will trigger the global MfaVerifyDialog.
 */
function ApiMfaBridge(): null {
  const { promptMfa } = useMfaVerifyPrompt();
  useEffect(() => {
    setMfaPromptHandler((opts) => promptMfa(opts));
    return () => setMfaPromptHandler(null);
  }, [promptMfa]);
  return null;
}

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <BrandingThemeProvider>
        <MfaVerifyProvider>
          <ApiMfaBridge />
          <RouterProvider router={router} />
        </MfaVerifyProvider>
      </BrandingThemeProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
